import { API } from '../../api'
import { crmServiceUrl } from '../../utils/constants'
import {
  isCanvasMigrationMockEnabled,
  mockGetCanvasMigrationStatus,
  mockPutCanvasMigration,
} from './canvasMigrationMock'

/**
 * Canvas migration control plane on CRM service:
 * PUT/GET /crm/v1/partners/:partner_id/salesforce-canvas-migrations
 *
 * PUT body must include Canvas `signed_request` so the control plane can call
 * permission-set metadata APIs. GET remains body-less (JWT only).
 */
const canvasMigrationPath = (partnerId: string) =>
  `${crmServiceUrl}/partners/${partnerId}/salesforce-canvas-migrations`

export type PutCanvasMigrationOptions = {
  /** Raw Salesforce Canvas signed_request (`<sig>.<base64-envelope>`). */
  signedRequest: string
}

export type GetCanvasMigrationStatusOptions = {
  /** Abort in-flight GETs when the poller stops or the page unmounts. */
  signal?: AbortSignal
}

export const getCanvasMigrationStatus = (
  partnerId: string,
  { signal }: GetCanvasMigrationStatusOptions = {}
) =>
  isCanvasMigrationMockEnabled
    ? mockGetCanvasMigrationStatus()
    : API.get(canvasMigrationPath(partnerId), { signal })

/**
 * Create, start, or retry the partner's single migration Job.
 * One PUT covers both first start and retry of failed/timed-out tasks.
 */
export const putCanvasMigration = (
  partnerId: string,
  { signedRequest }: PutCanvasMigrationOptions
) => {
  const trimmed = (signedRequest || '').trim()
  if (!trimmed) {
    throw new Error('signed_request is required')
  }
  if (isCanvasMigrationMockEnabled) {
    return mockPutCanvasMigration()
  }
  return API.put(canvasMigrationPath(partnerId), {
    signed_request: trimmed,
  })
}

/** @deprecated Prefer putCanvasMigration — start and retry share the same PUT. */
export const startCanvasMigration = putCanvasMigration

/** @deprecated Prefer putCanvasMigration — start and retry share the same PUT. */
export const retryCanvasMigration = putCanvasMigration
